/**
 * Derived insight values (property types, volume, area cards, trends, etc.)
 * from cityStats and popularAreaCards. Pure derivation, no effects.
 */

import { useMemo } from 'react';
import { pipe } from 'utils/pipe';
import type { CityStats } from 'data/insights/types';
import type { AreaCardData } from '../../local-focus/area-cards';
import type { PropertyTypeSegment, MarketVolumeData, PriceTrendPoint, MarketHealthData } from '../../local-focus/market-dynamics';
import type { AtAGlanceStats } from '../../local-focus/at-a-glance';
import type { InsightsListingType } from 'data/insights/types';
import { DEFAULT_VOLUME } from '../../local-focus/market-dynamics';
import { derivePropertyTypes, deriveVolume, deriveAreaCards } from './derivation';
import { STATIC_SQUARE_FOOTAGE_STATS } from 'utils/static-square-footage-stats';

const MIN_PROPERTY_TYPE_LISTINGS = 5;

export interface InsightsDerived {
  propertyTypes: PropertyTypeSegment[];
  marketVolume: MarketVolumeData | null;
  areaCardsData: AreaCardData[];
  priceTrends: PriceTrendPoint[];
  marketHealth: MarketHealthData | undefined;
  atAGlanceStats: AtAGlanceStats | null;
}

export function useInsightsDerived(
  cityStats: CityStats | null,
  popularAreaCards: AreaCardData[],
  currentSlug: string,
  hasLocationSignal: boolean,
  displayedListingCount?: number,
  // When the resolved location is itself a neighbourhood, the "Find Your Area"
  // cards are redundant — force them empty so the section is not rendered.
  suppressAreaCards = false,
  // Market the stats describe. Rent switches the volume card to monthly rent ranges;
  // rent and sold drop price per sq.ft, which only exists for for-sale listings.
  listingType: InsightsListingType = 'sale'
): InsightsDerived {
  const avgPricePerSqft = useMemo(() => {
    if (!cityStats || listingType !== 'sale') return 0;
    return STATIC_SQUARE_FOOTAGE_STATS[currentSlug]?.avgPricePerSqft || cityStats.AvgPricePerSqft || 0;
  }, [cityStats, currentSlug, listingType]);

  const propertyTypes = useMemo(() => {
    if (!cityStats) return [];
    const segments = derivePropertyTypes(cityStats);
    const totalListings = segments.reduce((sum, seg) => sum + seg.count, 0);
    return totalListings >= MIN_PROPERTY_TYPE_LISTINGS ? segments : [];
  }, [cityStats]);

  const marketVolume = useMemo(() => {
    if (!cityStats) return null;
    const vol = deriveVolume(cityStats, listingType);
    return vol === DEFAULT_VOLUME ? null : vol;
  }, [cityStats, listingType]);

  const areaCardsData = useMemo(() => {
    if (suppressAreaCards) return [];
    if (popularAreaCards.length > 0) return popularAreaCards;
    if (hasLocationSignal && cityStats) return deriveAreaCards(cityStats, currentSlug);
    return [];
  }, [popularAreaCards, hasLocationSignal, cityStats, currentSlug, suppressAreaCards]);

  const priceTrends = useMemo(() => {
    const raw = cityStats?.medianPriceTrends ?? [];
    return pipe(
      raw,
      (trends: typeof raw) => trends.filter(t => t.medianPrice > 0),
      filtered => filtered.map(t => ({ date: t.date, price: t.medianPrice }))
    );
  }, [cityStats]);

  const marketHealth = useMemo(() => {
    if (!cityStats) return undefined;
    const dom = cityStats.DaysOnMarket || 0;
    const slr = cityStats.SellListRatio || 0;
    const ppsf = avgPricePerSqft;
    // The for-sale report's "Median Rent" tile. The rent view already shows the median leased
    // rent (and the service hides it for small samples there), and it does not belong in sold.
    const rent = listingType === 'sale' ? cityStats.MedianRentPrice || 0 : 0;
    if (dom === 0 && slr === 0 && ppsf === 0 && rent === 0) return undefined;
    return { daysOnMarket: dom, sellListRatio: slr, avgPricePerSqft: ppsf, medianRentPrice: rent };
  }, [cityStats, avgPricePerSqft, listingType]);

  const atAGlanceStats = useMemo((): AtAGlanceStats | null => {
    if (!cityStats) return null;
    const total =
      (cityStats.TotalHouses || 0) +
      (cityStats.TotalCondos || 0) +
      (cityStats.TotalTownhouses || 0);
    if (total === 0) return null;
    return {
      avgHomePrice: cityStats.AvgTotalPrice || 0,
      avgHousePrice: cityStats.AvgHousePrice || 0,
      avgCondoPrice: cityStats.AvgCondoPrice || 0,
      avgTownhousePrice: cityStats.AvgTownhousePrice || 0,
      totalListings: displayedListingCount || total,
      medianPrice: cityStats.MedianPurchasePrice || 0,
      daysOnMarket: cityStats.DaysOnMarket || 0,
      sellListRatio: cityStats.SellListRatio || 0,
      avgPricePerSqft,
    };
  }, [cityStats, displayedListingCount, avgPricePerSqft]);

  return {
    propertyTypes,
    marketVolume,
    areaCardsData,
    priceTrends,
    marketHealth,
    atAGlanceStats,
  };
}
